// API + types for the Live Coach surface — "page 2" of the Interviewer Assistant. The realtime nudge
// stream and screen context arrive over a WebSocket (see `useInterviewIntelligenceWS`); the persisted
// trail (past nudges + the Q&A copilot history) is REST. Both talk to the Interview Intelligence
// service — a separate backend from core's `/api/v1` — so they go through `interviewIntelligenceHttp`.
//
// The shapes here mirror the AUTHORITATIVE Go contract: `actions/coach.go` + `models/coach_event.go`.
// Do not invent or rename fields.

import { interviewIntelligenceHttp as http } from '@/lib/interview-intelligence-http';

/* ----------------------------- the unified CoachEvent ----------------------------- */
// One row type for everything the coach emits — nudges and Q&A alike — distinguished by `category`.
// This is the REST trail row (`GET /coach/events` returns a flat array of these, id asc) and the
// answer returned by `POST /coach/ask`.
//
//   category: 0 time-budget · 1 coverage-gap · 2 probe · 3 drift · 4 talk-ratio · 5 wind-down
//             6 interviewer-question · 7 coach-answer
//
//   Nudge rows (0–5): title = the observation, text = the ready-to-say ask, citation = the why,
//                     acknowledged_at set once the nudge is auto-resolved in the conversation.
//   Q&A rows:         6 = question (text = the question); 7 = answer (text = the answer,
//                     citation = the grounding/source, parent_event_id = the question's id).
export type CoachCategory = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Category buckets that render as nudge cards on the LEFT stream.
export type NudgeCategory = 0 | 1 | 2 | 3 | 4 | 5;

export const CATEGORY_QUESTION = 6;
export const CATEGORY_ANSWER = 7;
// Coverage-progress events (the focus-area checklist driver) ride on the coverage-gap category.
export const CATEGORY_COVERAGE = 1;

export interface CoachEvent {
  id: number;
  account_id: number;
  session_id: number;
  category: CoachCategory;
  // For an answer (category 7), the id of the question (category 6) it responds to.
  parent_event_id: number | null;
  text: string;
  // The observation, for a nudge; null on Q&A rows.
  title: string | null;
  // The grounding — the "why" on a nudge, the source on an answer.
  citation: string | null;
  at_ms: number | null;
  // Set once a nudge is addressed in the conversation (auto-resolution). null while open.
  acknowledged_at: string | null;
  created_at: string;
}

export const isNudgeCategory = (
  category: CoachCategory
): category is NudgeCategory => category >= 0 && category <= 5;

/* ----------------------------- WebSocket events ----------------------------- */
// The realtime frames. Separate wire format from REST: every frame carries a `kind`, and the caller
// routes on it. ids and at_ms are NUMBERS (the interview-intelligence service broadcasts `"id": <int>`, `"at_ms": <int>`).

export type ScreenState = string;

// A lightweight recording-position tick — the panel only needs at_ms (to stamp nudges + feed the liveness
// watchdog). The full speaker/text stays server-side; they may be absent on the wire.
export interface TranscriptEvent {
  kind: 'transcript';
  at_ms: number;
  text?: string;
  speaker?: string | null;
}
export interface ScreenStateEvent {
  kind: 'screen.state';
  screen_state: ScreenState;
  what_changed: string;
  at_ms?: number;
}
export interface CoachNudgeEvent {
  kind: 'coach.nudge';
  id: number;
  category: NudgeCategory;
  // The observation — what the coach noticed.
  title: string;
  // The ready-to-say ask — phrased so the interviewer can voice it as-is.
  text: string;
  // The citation/grounding — why this nudge fired (scorecard area, prep focus, …).
  why: string;
  at_ms: number;
}
export interface CoachQuestionEvent {
  kind: 'coach.question';
  id: number;
  text: string;
  at_ms: number;
}
export interface CoachAnswerEvent {
  kind: 'coach.answer';
  id: number;
  // Threads this answer under the question it responds to.
  parent_id: number;
  text: string;
  // The grounding to render as a citation under the answer.
  source?: string | null;
  at_ms: number;
}
export interface CoachResolvedEvent {
  kind: 'coach.resolved';
  // The nudge that was addressed in the conversation — mark it addressed in the stream.
  id: number;
}
export interface CoachCoverageEvent {
  kind: 'coach.coverage';
  id: number;
  category: 1;
  // The focus area(s) newly marked covered by this event (the card headline).
  area: string;
  // The full covered / remaining split (focus-area names) at this moment — drives the rail checklist.
  covered: string[];
  remaining: string[];
  // The ready-to-read progress line ("✓ X covered. Still to assess: Y, Z").
  text: string;
  at_ms: number;
}

// Transient (not persisted): the coach is running a tool while answering a question — drives the "Pulling
// the scorecard…" line under the pending copilot turn. Threaded to the question by parent_id.
export interface CoachToolUseEvent {
  kind: 'coach.tool_use';
  parent_id: number;
  // The tool the coach is running (e.g. 'get_scorecard').
  tool: string;
  // The human-facing progress line ("Pulling the scorecard…").
  label: string;
  at_ms: number;
}

// The meeting feed's health, published by the owning task on ingest connect/disconnect. The panel's OWN ws
// stays healthy while Recall's stream is up or down, so this is the only way the FE knows the feed dropped.
export interface StreamStatusEvent {
  kind: 'stream.live' | 'stream.lost';
}

// A server keepalive heartbeat (~54s) — the only periodic frame now that transcripts aren't streamed. It
// exists purely to feed the stale-frame watchdog + keep the LB warm; the reducer ignores it.
export interface HeartbeatEvent {
  kind: 'ping';
}

export type CoachWSEvent =
  | TranscriptEvent
  | ScreenStateEvent
  | CoachNudgeEvent
  | CoachQuestionEvent
  | CoachAnswerEvent
  | CoachResolvedEvent
  | CoachCoverageEvent
  | CoachToolUseEvent
  | StreamStatusEvent
  | HeartbeatEvent;

/* ----------------------------- REST ----------------------------- */

type SessionId = number | string;

// The interview-intelligence session record. `started_at` is set when the meeting goes live (first
// transcript); `ended_at` when it ends. These anchor the transport's meeting timer.
export interface CoachSession {
  id: number;
  status: number; // 0 scheduled · 1 live · 2 ended · 3 failed
  started_at: string | null;
  ended_at: string | null;
}

// Fetch the session (account-scoped). Same interview-intelligence-client guard as listCoachEvents: a 4xx resolves to an error
// object (with `status` but no `id`), so accept only a real session object.
export const getSession = async (
  sessionId: SessionId
): Promise<CoachSession | null> => {
  const result = (await http.get(`/sessions/${sessionId}`)) as unknown;
  return result && typeof result === 'object' && 'id' in result
    ? (result as CoachSession)
    : null;
};

// The persisted trail — every CoachEvent for the session, id asc. Seeds both the LEFT stream (nudge rows
// 0–5) and the RIGHT copilot (Q&A rows 6/7).
//
// IMPORTANT — `interviewIntelligenceHttp` quirks (these caused the chat to silently break):
//  • Its SUCCESS interceptor returns `result.data`, so a 2xx resolves to the body directly. Reading `.data`
//    again double-unwraps to `undefined` → React Query throws "data cannot be undefined" and the whole trail
//    is poisoned (answers never thread).
//  • Its auth interceptor RESOLVES a 4xx as an error object (e.g. an ended session → 404), not a rejection.
// So: accept the value only if it's actually an array; anything else (error object / empty) → []. Never `.data`.
export const listCoachEvents = async (
  sessionId: SessionId
): Promise<CoachEvent[]> => {
  const result = (await http.get(
    `/sessions/${sessionId}/coach/events`
  )) as unknown;
  return Array.isArray(result) ? (result as CoachEvent[]) : [];
};

// Ask the coach a question. Returns the ANSWER event (category 7) — the resolved value is already the body.
// A 4xx (e.g. the session is no longer live) comes back as an object with an injected `status`; surface that
// as a failure so the copilot shows an error instead of threading a phantom answer.
export const askCoach = async (
  sessionId: SessionId,
  question: string,
  atMs: number
): Promise<CoachEvent> => {
  const result = (await http.post(`/sessions/${sessionId}/coach/ask`, {
    question,
    at_ms: atMs,
  })) as { status?: number } | CoachEvent;
  if (
    result &&
    typeof result === 'object' &&
    Number((result as any).status) >= 400
  ) {
    throw result;
  }
  return result as CoachEvent;
};

/* ----------------------------- copilot turns ----------------------------- */

// A question paired with its answer, derived from the flat trail for the RIGHT copilot. `answer` is
// null until the coach responds (or while a turn is optimistic).
export interface CoachTurn {
  // The question event id, or a synthetic key for an optimistic (not-yet-persisted) turn.
  key: string;
  question: string;
  answer: string | null;
  // The answer's grounding/source, rendered as a citation.
  source: string | null;
}

// Build the copilot turns from the flat trail: each category-6 event is a question; its answer is the
// category-7 event whose `parent_event_id` points back to it. Ordered by the question's id (asc).
export const buildTurns = (events: CoachEvent[]): CoachTurn[] => {
  const answersByParent = new Map<number, CoachEvent>();
  events.forEach((e) => {
    if (e.category === CATEGORY_ANSWER && e.parent_event_id !== null) {
      answersByParent.set(e.parent_event_id, e);
    }
  });
  return events
    .filter((e) => e.category === CATEGORY_QUESTION)
    .sort((a, b) => a.id - b.id)
    .map((q) => {
      const answer = answersByParent.get(q.id);
      return {
        key: String(q.id),
        question: q.text,
        answer: answer ? answer.text : null,
        source: answer ? answer.citation : null,
      };
    });
};

/* ----------------------------- helpers ----------------------------- */

// The category → card identity (label + accent palette token + icon key) for the LEFT stream. Colour
// names are Kula theme `pastel-*` tokens so the cards stay on-palette across light/dark.
export interface NudgeStyle {
  tag: string;
  icon: NudgeIconKey;
  // Kula theme palette family used for the card's accent (chip bg/text + rail icon).
  palette:
    | 'pastel-1'
    | 'pastel-2'
    | 'pastel-3'
    | 'pastel-4'
    | 'pastel-5'
    | 'pastel-6';
  // Proactive pacing/coverage signals (time budget, coverage gap, drift, wind-down) colour the WHOLE card to
  // grab attention; reactive probes (and the soft stall prompt, which rides the probe category) stay neutral.
  attention: boolean;
}
export type NudgeIconKey =
  | 'time'
  | 'coverage'
  | 'probe'
  | 'drift'
  | 'talk'
  | 'wind';

export const NUDGE_STYLES: Record<NudgeCategory, NudgeStyle> = {
  0: { tag: 'Time budget', icon: 'time', palette: 'pastel-6', attention: true },
  1: {
    tag: 'Coverage gap',
    icon: 'coverage',
    palette: 'pastel-4',
    attention: true,
  },
  2: { tag: 'Probe', icon: 'probe', palette: 'pastel-1', attention: false },
  3: { tag: 'Drift', icon: 'drift', palette: 'pastel-2', attention: true },
  4: { tag: 'Talk ratio', icon: 'talk', palette: 'pastel-3', attention: false },
  5: { tag: 'Wind down', icon: 'wind', palette: 'pastel-5', attention: true },
};

export const nudgeStyle = (category: NudgeCategory): NudgeStyle =>
  NUDGE_STYLES[category] ?? NUDGE_STYLES[2];

// at_ms → "mm:ss" (or "h:mm:ss" for long rounds), the elapsed-time label on a card / the transport.
export const formatClock = (ms?: number | null): string => {
  if (ms === null || ms === undefined || ms < 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};
